// MD5 (RFC 1321) — required for HTTP Digest Authentication
function md5(str: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    if (c < 0x80) bytes.push(c);
    else if (c < 0x800) bytes.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    else bytes.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
  }
  const len = bytes.length;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const lo = (len * 8) >>> 0;
  const hi = Math.floor(len / 0x20000000) >>> 0;
  for (let i = 0; i < 4; i++) bytes.push((lo >>> (i * 8)) & 0xff);
  for (let i = 0; i < 4; i++) bytes.push((hi >>> (i * 8)) & 0xff);

  const add = (x: number, y: number) => (x + y) | 0;
  const rol = (v: number, n: number) => (v << n) | (v >>> (32 - n));
  const K = Array.from({ length: 64 }, (_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000) >>> 0);
  const S = [7,12,17,22,7,12,17,22,7,12,17,22,7,12,17,22,
             5,9,14,20,5,9,14,20,5,9,14,20,5,9,14,20,
             4,11,16,23,4,11,16,23,4,11,16,23,4,11,16,23,
             6,10,15,21,6,10,15,21,6,10,15,21,6,10,15,21];

  let a0 = 0x67452301, b0 = 0xefcdab89 | 0, c0 = 0x98badcfe | 0, d0 = 0x10325476;

  for (let bi = 0; bi < bytes.length; bi += 64) {
    const M: number[] = [];
    for (let j = 0; j < 16; j++)
      M[j] = bytes[bi+j*4] | (bytes[bi+j*4+1] << 8) | (bytes[bi+j*4+2] << 16) | (bytes[bi+j*4+3] << 24);

    let A = a0, B = b0, C = c0, D = d0;
    for (let i = 0; i < 64; i++) {
      let F: number, g: number;
      if (i < 16)      { F = (B & C) | (~B & D); g = i; }
      else if (i < 32) { F = (D & B) | (~D & C); g = (5*i + 1) % 16; }
      else if (i < 48) { F = B ^ C ^ D;           g = (3*i + 5) % 16; }
      else             { F = C ^ (B | ~D);         g = (7*i) % 16; }
      const tmp = D; D = C; C = B;
      B = add(B, rol(add(add(A, F), add(M[g], K[i])), S[i]));
      A = tmp;
    }
    a0 = add(a0, A); b0 = add(b0, B); c0 = add(c0, C); d0 = add(d0, D);
  }

  const le = (n: number) => {
    n = n >>> 0;
    return [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]
      .map(b => b.toString(16).padStart(2, '0')).join('');
  };
  return le(a0) + le(b0) + le(c0) + le(d0);
}

function newSearchId(): string {
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).toUpperCase().padStart(4, '0');
  return `${hex()}${hex()}-${hex()}-${hex()}-${hex()}-${hex()}${hex()}${hex()}`;
}

export interface HikvisionSegment {
  sourceID: string;
  trackID: number;
  timeSpan: { startTime: string; endTime: string };
  mediaSegmentDescriptor: {
    codecType: string;
    contenType: string;
    playbackURI: string;
    size: number;
  };
}

export interface HikvisionSearchResult {
  searchID: string;
  responseStatus: boolean;
  responseStatusStrg: 'OK' | 'MORE' | 'NO MATCHES';
  numOfMatches: number;
  matchList: { searchMatchItem: HikvisionSegment }[];
}

/**
 * @param workerUrl - URL of the Cloudflare Worker proxy, e.g.
 *                    "https://gateway.jsrastreamento.com/hik-proxy"
 * All auth and NVR communication is handled server-side by the Worker.
 */
export async function searchHikvisionRecordings(
  workerUrl: string,
  baseUrl: string,
  devIndex: string,
  user: string,
  pass: string,
  trackID: number,
  startTime: string,
  endTime: string,
  searchResultPostion = 0,
  maxResults = 40,
  searchID = newSearchId(),
): Promise<HikvisionSearchResult> {
  const res = await fetch(workerUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      baseUrl, devIndex, user, pass, trackID,
      startTime, endTime, searchResultPostion, maxResults, searchID,
    }),
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (!data.CMSearchResult) throw new Error(data.statusString ?? 'Unexpected response');
  return data.CMSearchResult as HikvisionSearchResult;
}
